import { useState, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Save, GripVertical } from 'lucide-react';
import { Service, ServiceCategory as Category, Unit, Recipe, RecipeLayer } from '@/shared/model';
import { ResponsiveModal } from '@/shared/ui/responsive-modal';
import { Button, Input } from '@/shared/ui/base';
import { useUIStore } from '@/stores';

interface RecipeEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipe: Recipe | null;
  category: string;
  onSave: (recipe: Recipe, category: string) => void;
  services: Service[];
  categories: Category[];
  units: Unit[];
}

export const RecipeEditorModal: FC<RecipeEditorModalProps> = ({ 
    isOpen, 
    onClose, 
    recipe, 
    category,
    onSave,
    services = [],
    categories = [],
    units = []
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(recipe?.name || '');
  const [layers, setLayers] = useState<RecipeLayer[]>(recipe?.layers || []);
  const openModal = useUIStore((s) => s.openModal);

  const handleAddService = (service: Service) => {
    const newLayer: RecipeLayer = {
      serviceId: service.id,
      coefficient: 1
    };
    setLayers([...layers, newLayer]);
  };

  const handleUpdateLayer = (idx: number, updates: Partial<RecipeLayer>) => {
    setLayers(layers.map((l, i) => i === idx ? { ...l, ...updates } : l));
  };

  const handleRemoveLayer = (idx: number) => {
    setLayers(layers.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    if (!name.trim()) return;
    const updatedRecipe: Recipe = {
      id: recipe?.id || `recipe_${Date.now()}`,
      name,
      layers
    };
    onSave(updatedRecipe, category);
    onClose();
  };

  return (
    <ResponsiveModal
        open={isOpen}
        onOpenChange={(open) => !open && onClose()}
        title={recipe ? t('recipes.editor.edit_title', 'Редактировать шаблон') : t('recipes.editor.create_title', 'Новый шаблон')}
        className="max-w-3xl"
        stickyHeader
    >
        <div className="space-y-6">
            <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">{t('recipes.editor.name_label', 'Название шаблона')}</label>
                <Input 
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('recipes.editor.name_placeholder', 'Напр. Стены под покраску (Эталон)')}
                    className="h-12 font-bold text-lg"
                />
            </div>

            <div className="space-y-3">
                <div className="flex justify-between items-center">
                    <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1">{t('recipes.editor.layers_label', 'Состав работ')}</label>
                    <Button 
                        size="sm" 
                        variant="secondary" 
                        onClick={() => {
                          openModal('service_selector', {
                            target: 'recipe',
                            recipeId: recipe?.id || `recipe_${Date.now()}`,
                            category,
                            returnTo: 'recipe_editor',
                            draftRecipe: {
                              id: recipe?.id || `recipe_${Date.now()}`,
                              name,
                              layers
                            }
                          });
                        }}
                        className="h-8 text-[10px] font-bold"
                    >
                        <Plus size={14}/> {t('recipes.editor.add_service', 'ДОБАВИТЬ УСЛУГУ')}
                    </Button>
                </div>

                <div className="border border-slate-100 dark:border-slate-800 rounded-2xl overflow-hidden bg-slate-50/50 dark:bg-slate-900/50">
                    {layers.length === 0 ? (
                        <div className="py-12 text-center text-slate-400 text-sm italic">
                            {t('recipes.editor.empty_layers', 'Добавьте услуги, чтобы сформировать пакет')}
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                            {layers.map((layer, idx) => {
                                const service = services.find(s => s.id === layer.serviceId);
                                if (!service) return null;

                                return (
                                    <div key={idx} className="flex items-center gap-3 p-3 bg-white dark:bg-slate-900 group">
                                        <div className="text-slate-300 dark:text-slate-700 cursor-grab active:cursor-grabbing">
                                            <GripVertical size={18}/>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-sm text-slate-700 dark:text-slate-200 truncate">
                                                {service.nameKey ? t(service.nameKey, service.name) : service.name}
                                            </div>
                                            <div className="text-[10px] text-slate-400 uppercase font-medium">{service.unit}</div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="text-[10px] font-bold text-slate-400 uppercase">{t('recipes.editor.norm', 'Норма')}:</div>
                                            <input 
                                                type="number"
                                                value={layer.coefficient}
                                                onChange={(e) => handleUpdateLayer(idx, { coefficient: parseFloat(e.target.value) || 0 })}
                                                className="w-16 h-8 bg-slate-100 dark:bg-slate-800 border-none rounded-lg text-center font-bold text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                            />
                                        </div>
                                        <button 
                                            onClick={() => handleRemoveLayer(idx)}
                                            className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all"
                                        >
                                            <Trash2 size={16}/>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                <Button variant="ghost" onClick={onClose}>{t('common.cancel', 'Отмена')}</Button>
                <Button 
                    variant="primary" 
                    onClick={handleSave}
                    disabled={!name.trim() || layers.length === 0}
                    className="px-8"
                >
                    <Save size={18}/> {t('common.save', 'Сохранить шаблон')}
                </Button>
            </div>
        </div>


    </ResponsiveModal>
  );
};
